import React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type SidebarItemProps = {
  item: {
    icon?: React.ReactNode;
    label: string;
    route?: string;
    badgeLabel?: string;
  };
  notificationCount?: number;
  onClick?: () => void;
  hasSelectedSubItem?: boolean;
  variant?: "primary" | "sub";
  className?: string;
  /** Icon-only rendering for the desktop rail; the label moves to a tooltip. */
  collapsed?: boolean;
};

const TOOLTIP_LABEL_SEPARATOR = " · ";

export function SidebarItem({
  item,
  notificationCount = 0,
  hasSelectedSubItem = false,
  onClick,
  variant = "primary",
  className,
  collapsed = false,
}: SidebarItemProps) {
  const router = useRouterState();

  const isActive = (item: SidebarItemProps["item"]) => {
    if (
      item.route &&
      router.location.pathname.startsWith(item.route) &&
      !hasSelectedSubItem
    ) {
      return true;
    }
    return false;
  };

  const isItemActive = isActive(item);
  const isParentActive = hasSelectedSubItem;
  const isChildActive = isParentActive && !isItemActive;
  const isActiveState = isItemActive || isChildActive;

  const baseClasses = cn(
    "group relative flex min-h-12 w-full min-w-0 items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
    variant === "primary" ? "text-base font-semibold" : "text-sm font-medium",
    isActiveState
      ? "border-primary/15 bg-primary/5 text-primary"
      : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900",
    collapsed && "justify-center px-0",
    className,
  );

  const iconClasses = cn(
    "relative flex shrink-0 items-center justify-center transition-colors",
    variant === "primary" ? "size-6 [&>svg]:size-5" : "size-5 [&>svg]:size-4",
    isActiveState
      ? "text-primary"
      : "text-slate-500 group-hover:text-slate-700",
  );

  const Content = (
    <>
      {item.icon ? (
        <span className={iconClasses}>
          {item.icon}
          {notificationCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex min-h-[16px] min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-[5px] text-[10px] font-semibold text-white">
              {notificationCount}
            </span>
          )}
        </span>
      ) : null}
      {collapsed ? (
        <span className="sr-only">{item.label}</span>
      ) : (
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 max-w-full whitespace-normal break-words leading-5">
            {item.label}
          </span>
          {item.badgeLabel && (
            <span
              className={cn(
                "ml-auto shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                isActiveState
                  ? "border-amber-200 bg-amber-100 text-amber-900"
                  : "border-amber-300 bg-amber-100 text-amber-900",
              )}
            >
              {item.badgeLabel}
            </span>
          )}
        </span>
      )}
    </>
  );

  const tooltipLabel = item.badgeLabel
    ? `${item.label}${TOOLTIP_LABEL_SEPARATOR}${item.badgeLabel}`
    : item.label;

  const withCollapsedTooltip = (trigger: React.ReactElement) => {
    if (!collapsed) {
      return trigger;
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="right">{tooltipLabel}</TooltipContent>
      </Tooltip>
    );
  };

  if (!item.route) {
    return withCollapsedTooltip(
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onClick?.();
        }}
        className={baseClasses}
        aria-label={item.label}
      >
        {Content}
      </a>,
    );
  }

  return withCollapsedTooltip(
    <Link
      to={item.route}
      onClick={onClick}
      className={baseClasses}
      aria-current={isActiveState ? "page" : undefined}
    >
      {Content}
    </Link>,
  );
}

export default SidebarItem;
